import * as React from 'react';
import { ApolloError } from 'apollo-client';
import { Query } from 'react-apollo';
import update from 'immutability-helper';

import GET_COACH_LIST_QUERY from 'coaching/queries/GetCoachList.gql';
import {
  GetCoachList,
  GetCoachList_coaching_getCoachList_coaches,
} from 'coaching/queries/__generated__/GetCoachList';
import { useRef } from 'react';

export type Props = {
  children: (props: {
    coaches?: GetCoachList_coaching_getCoachList_coaches[];
    loading: boolean;
    error?: ApolloError;
    onNextPage: (after?: string | null) => Promise<void>;
    endCursor?: string | null;
    hasNextPage?: boolean;
  }) => React.ReactNode;
  query: string;
  skipQuery?: boolean;
};

export default function GetCoachesContainer(props: Props) {
  const currentNameQuery = useRef<string>('');
  return (
    <Query<GetCoachList>
      query={GET_COACH_LIST_QUERY}
      variables={{ nameQuery: props.query }}
      skip={props.skipQuery}>
      {({ loading, error, data, fetchMore, ...others }) => {
        currentNameQuery.current = others.variables.nameQuery;

        const handleNextPage = async (after?: string | null) => {
          await fetchMore({
            variables: {
              after,
              nameQuery: props.query,
            },
            updateQuery: (previousResult, { fetchMoreResult, variables }) => {
              if (!fetchMoreResult) {
                return previousResult;
              }
              if (currentNameQuery.current !== variables?.nameQuery) {
                return previousResult;
              }

              const existingCoaches = previousResult.coaching?.getCoachList?.coaches ?? [];
              const newCoaches = fetchMoreResult.coaching?.getCoachList?.coaches ?? [];

              return update(previousResult, {
                coaching: {
                  getCoachList: {
                    pageInfo: {
                      $set: fetchMoreResult.coaching?.getCoachList.pageInfo,
                    },
                    coaches: { $set: [...existingCoaches, ...newCoaches] },
                  },
                },
              });
            },
          });
        };

        return props.children({
          coaches: data?.coaching?.getCoachList?.coaches,
          loading,
          error,
          endCursor: data?.coaching?.getCoachList?.pageInfo?.endCursor,
          hasNextPage: data?.coaching?.getCoachList?.pageInfo?.hasNextPage,
          onNextPage: handleNextPage,
        });
      }}
    </Query>
  );
}
